/**
 * Line quantities, now that a half plate is an ordinary punch.
 *
 * `order_items.quantity` is Numeric(10,2) on the server: the restaurant punches half
 * plates many times a day, so 0.5 is a real quantity rather than a rounding slip. Two
 * rules hold everywhere the till touches a quantity:
 *
 *  - it is a number with at most 2 decimals, greater than zero;
 *  - an integral quantity renders exactly as it always did — "2", never "2.0" — so
 *    KOTs, bills and every existing screen read the same string they read before.
 *
 * Everything here is display/entry only. The bill that prints is still the server's
 * preview: `0.5 * 300` on this side is a courtesy, not the price.
 */

/** Smallest quantity the server accepts (2dp, > 0). */
export const MIN_QTY = 0.25;

/** The half plate itself — the fraction the till actually produces. */
export const HALF_QTY = 0.5;

/**
 * Coerce anything the UI or the API hands us into a 2dp number.
 *
 * Strings arrive from number pads and from Decimal-as-string API fields, so parseFloat
 * (never parseInt — that reads "0.5" as 0) then snap to paise-grade precision: summing
 * fractions in binary floating point otherwise shows 1.5000000000000002 on a bill pad.
 */
export const roundQty = value => {
  const parsed = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

/** Add quantities without floating-point residue. */
export const sumQty = values => roundQty(values.reduce((total, value) => total + roundQty(value), 0));

/**
 * Render a quantity: "2" for two plates, "0.5" for a half one, "1.5" for one and a
 * half. Trailing zeros are dropped by Number's own formatting, which is exactly the
 * no-churn behaviour the contract asks for.
 */
export const fmtQty = value => String(roundQty(value));

/** Line total for display. The printed number remains the server's. */
export const lineTotal = (unitPrice, quantity) =>
  Math.round(Number(unitPrice) * roundQty(quantity) * 100) / 100;
